import { CONTEXT_SECTION_TITLES } from '@lixionary-jira/domain';
import { Link } from 'react-router';
import { Badge } from '../../../ui/Badge';
import { ActorName, Card, KeyValue } from '../../../ui/misc';
import { Timestamp } from '../../../ui/Timestamp';
import type { ImplDetail } from './types';

export function ClaimDetails({ i, projectKey }: { i: ImplDetail; projectKey: string | undefined }) {
  const key = projectKey;
  return (
    <>
      <Card title="Claim" tight>
        <KeyValue
          items={[
            [
              'Owner',
              <span key="o">
                <ActorName actor={i.owner} />
              </span>,
            ],
            ['Generation', i.generation],
            [
              'Builder',
              i.builder
                ? `${i.builder.name} · ${i.builder.machineId}`
                : i.ownerKind === 'human'
                  ? 'Manual'
                  : '—',
            ],
            [
              'Acceptance',
              i.acceptance ? `${i.acceptance.displayId} · scope rev ${i.acceptance.scopeRevision}` : '—',
            ],
            ['Started', <Timestamp key="s" value={i.startedAt} />],
            [
              'Last heartbeat',
              i.ownerKind === 'ai' ? <Timestamp value={i.lastHeartbeatAt} /> : 'Not used for manual work',
            ],
            ['Lease expires', i.ownerKind === 'ai' ? <Timestamp value={i.leaseExpiresAt} /> : '—'],
            ['Ended', <Timestamp key="e" value={i.endedAt} />],
            ['Termination', i.terminationReason ?? '—'],
          ]}
        />
      </Card>
      <Card title="Context versions used" tight>
        <ul className="checklist">
          {i.contextVersions.map((c) => (
            <li key={c.section} style={{ justifyContent: 'space-between' }}>
              <span>{CONTEXT_SECTION_TITLES[c.section]}</span>
              {c.number ? (
                <Link
                  to={`/projects/${key}/settings/context/${c.section}/versions/${c.number}`}
                  className="mono"
                >
                  v{c.number}
                </Link>
              ) : (
                <span className="muted">—</span>
              )}
              {!c.current && c.currentNumber && <Badge tone="warning">Current v{c.currentNumber}</Badge>}
            </li>
          ))}
        </ul>
      </Card>
      <Card
        title="Workspace (reported)"
        tight
        subtitle="Reported metadata; the server cannot inspect the machine."
      >
        <KeyValue
          items={[
            [
              'Branch',
              <span key="b" className="mono small">
                {i.workspace.branch ?? '—'}
              </span>,
            ],
            [
              'Base revision',
              <span key="r" className="mono small">
                {i.workspace.baseRevision ?? '—'}
              </span>,
            ],
            [
              'Worktree',
              <span key="w" className="mono small">
                {i.workspace.worktree ?? '—'}
              </span>,
            ],
            [
              'Directory',
              <span key="d" className="mono small">
                {i.workspace.workDirectory ?? '—'}
              </span>,
            ],
            ['Retained from', i.retainedFrom ?? '—'],
          ]}
        />
      </Card>
      {i.recovery && (
        <Card title="Recovery" tight>
          <KeyValue
            items={[
              ['Decision', i.recovery.choice],
              ['By', <ActorName key="a" actor={i.recovery.admin} />],
              ['When', <Timestamp key="w" value={i.recovery.at} />],
              ['Workspace', i.recovery.workspaceAssessment],
              ['Reason', i.recovery.reason],
            ]}
          />
        </Card>
      )}
      {i.exceptions.length > 0 && (
        <Card title="Approved exceptions" tight>
          <ul className="checklist">
            {i.exceptions.map((e) => (
              <li key={e.id} className="small">
                {e.testName === '*' ? 'No tests' : e.testName}: “{e.reason}” — <ActorName actor={e.admin} />
              </li>
            ))}
          </ul>
        </Card>
      )}
    </>
  );
}
